/**
 * WeaveIn.ai Platform - Member List Component (Tailwind CSS)
 * Displays members with pagination, search, and filtering
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  CheckCircle,
  AlertCircle,
  Brain,
  Loader2,
  Lock,
  Star,
  Users,
  ArrowUp,
  ArrowDown,
  Archive,
} from 'lucide-react';
import { ShieldedField, ShieldIndicator } from '../security/ShieldedField';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Pagination } from '../common/Pagination';
import { selectCoordinatorCandidates } from './coordinatorCandidates';
import useMembers from '@/hooks/useMembers';
import { useMemberFavorites } from '@/hooks/useMemberFavorites';
import { apiClient } from '@/services/apiClient';
import { Member, MemberListColumn, MemberAdvancedFilter, membersAPI } from '@/services/api/members';
import { getActiveCboId } from '@/services/api/organizations';
import { formatDateOnly } from '@/utils/dateTime';
import { orderOptions } from '@/utils/optionOrder';
import { authFetch } from '@/lib/authFetch';

import { config as productConfig } from '@/src/config';
import {
  distinctPlanValues,
  hasConfiguredLanguageFilter,
  orgUsesInsuranceIds,
} from './memberListViewFields';
import { MemberListViewCustomizer, useMemberListViewFields } from './MemberListViewCustomizer';
interface MemberListProps {
  onMemberSelect?: (member: Member) => void;
  onNavigate?: (tab: string, memberId?: number) => void;
  pageSize?: number;
  favoriteIds?: number[] | null;
  onToggleFavorites?: () => void;
}

interface AIEnrichmentStatus {
  has_enrichment: boolean;
  confidence_score: number | null;
  last_enriched_at: string | null;
  status: 'completed' | 'running' | 'failed' | null;
}

interface MemberPlanLookup {
  member_id: number;
  lob_name: string | null;
  lob_type: string | null;
  product_name: string | null;
  product_code: string | null;
  plan_kind: 'plan' | 'employer_plan' | null;
}

/**
 * Custom field filter — org-scoped dropdown of field names + value picker.
 * Format: "field_name=value" sent to backend.
 */
function CustomFieldFilter({ value, onChange }: { value?: string; onChange: (v: string | undefined) => void }) {
  const [orgFields, setOrgFields] = React.useState<{ name: string; friendly_name: string; type: string; category?: string; field_options?: { value: string; label: string }[] }[]>([]);
  const [selectedField, setSelectedField] = React.useState('');
  const [selectedValue, setSelectedValue] = React.useState('');

  React.useEffect(() => {
    apiClient.get('/api/custom-fields')
      .then(r => setOrgFields(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (value && value.includes('=')) {
      const [f, v] = value.split('=', 2);
      setSelectedField(f);
      setSelectedValue(v);
    }
  }, [value]);

  const currentFieldDef = orgFields.find(f => f.name === selectedField);
  const hasOptions = currentFieldDef?.field_options && currentFieldDef.field_options.length > 0;

  const applyFilter = (field: string, val: string) => {
    if (field && val) {
      onChange(`${field}=${val}`);
    } else {
      onChange(undefined);
    }
  };

  return (
    <div className="space-y-1">
      <select
        value={selectedField}
        onChange={(e) => {
          setSelectedField(e.target.value);
          setSelectedValue('');
          if (!e.target.value) onChange(undefined);
        }}
        className="w-full h-8 text-xs border border-gray-300 rounded-lg px-2 bg-white"
      >
        <option value="">All Fields</option>
        {orgFields.map(f => (
          <option key={f.name} value={f.name}>
            {f.friendly_name} {f.category ? `(${f.category})` : ''}
          </option>
        ))}
      </select>
      {selectedField && (
        hasOptions ? (
          <select
            value={selectedValue}
            onChange={(e) => { setSelectedValue(e.target.value); applyFilter(selectedField, e.target.value); }}
            className="w-full h-8 text-xs border border-gray-300 rounded-lg px-2 bg-white"
          >
            <option value="">Any value</option>
            {orderOptions(currentFieldDef?.field_options).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <Input
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            onBlur={() => applyFilter(selectedField, selectedValue)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilter(selectedField, selectedValue); }}
            placeholder="Enter value..."
            className="h-8 text-xs"
          />
        )
      )}
    </div>
  );
}

export default function MemberListNew({ onMemberSelect, onNavigate, pageSize = 50, favoriteIds, onToggleFavorites }: MemberListProps) {
  // Debug removed — v2 props confirmed working
  const { t } = useAppTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [qualityFilter, setQualityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [filterLob, setFilterLob] = useState<string | undefined>();
  const [filterPlan, setFilterPlan] = useState<string | undefined>();
  const [aiEnrichmentData, setAiEnrichmentData] = useState<Record<number, AIEnrichmentStatus>>({});
  const [planLookup, setPlanLookup] = useState<Record<number, MemberPlanLookup>>({});

  // Advanced filters (Phase 2)
  const [filterProgramId, setFilterProgramId] = useState<number | undefined>();
  const [filterEnrollStatus, setFilterEnrollStatus] = useState<string | undefined>();
  const [filterGender, setFilterGender] = useState<string | undefined>();
  const [filterLanguage, setFilterLanguage] = useState<string | undefined>();
  const [filterInsurance, setFilterInsurance] = useState<string | undefined>();
  const [filterCustomField, setFilterCustomField] = useState<string | undefined>();
  const [filterCoordinator, setFilterCoordinator] = useState<number | undefined>();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Name-column sort (TRUS-550). The roster has always come back alphabetical by
  // last name; what was missing was any way to SEE that, or to change it. Held as
  // last_name|first_name + asc|desc and passed to the API rather than sorted in the
  // browser, because the list is paginated — a client-side sort would only reorder
  // the 25 rows on screen and quietly lie about the other 1,300.
  const [sortBy, setSortBy] = useState<'last_name' | 'first_name'>('last_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // LCC only for now (TODO: productize via nav registry) — workforce members
  // (is_workforce) get a badge + a "Workforce only" filter toggle.
  const isLccWorkforce = Number(getActiveCboId()) === 350;
  const [workforceOnly, setWorkforceOnly] = useState(false);
  const [programs, setPrograms] = useState<{ id: number; name: string }[]>([]);
  const [orgUsers, setOrgUsers] = useState<{ id: number; name: string; role?: string }[]>([]);

  // Per-CBO filter chips — config served by GET /api/members/filter-chips
  interface FilterChipConfig {
    key: string;
    label: string;
    description?: string;
    icon?: string;
    color?: string;
    sort_order: number;
    default_on: boolean;
    /**
     * How many members this chip matches for the current user. Served by
     * GET /api/members/filter-chips?include_counts=true.
     *
     * Shown on the chip because a filter that matches nothing is otherwise
     * indistinguishable from one that works — IFR ran three chips keyed on a custom
     * field no member carried and nobody could tell for four months (TRUS-559).
     * `null` means the count could not be computed, not zero.
     */
    count?: number | null;
    /** False when the stored predicate could not be compiled at all. */
    predicate_ok?: boolean;
  }
  const [filterChips, setFilterChips] = useState<FilterChipConfig[]>([]);
  const [selectedChipKey, setSelectedChipKey] = useState<string | undefined>();
  // The archive view. Deleting a member only archives them — the record and all of
  // its data survive and `POST /api/members/{id}/restore` puts it back. But until this
  // toggle existed there was no way to SEE an archived member, so the restore could
  // never be reached and staff filed support tickets for records they had archived
  // themselves (TKT-20260902-59BC4E). Deliberately a plain toggle on the list rather
  // than a configurable filter chip: recovery must work in every org out of the box.
  const [showArchived, setShowArchived] = useState(false);
  // Org-configured list columns (Field Config → "Show in list"). When non-empty
  // the table renders these dynamically; otherwise it falls back to the legacy
  // fixed columns so orgs that haven't configured anything are unaffected.
  const [listColumns, setListColumns] = useState<MemberListColumn[]>([]);
  const [advancedFilterDefs, setAdvancedFilterDefs] = useState<MemberAdvancedFilter[]>([]);
  // Hidden core fields: keys whose admin has flagged is_hidden=true in
  // member_field_configurations. When a key lives here we suppress the
  // matching hardcoded filter dropdown below — admins who hide a field
  // from the profile don't want it reappearing as a filter.
  const [hiddenCoreKeys, setHiddenCoreKeys] = useState<Set<string>>(new Set());
  // TRUS-568: the hardcoded Insurance filter matches on medicaid_id /
  // medicare_id / commercial_insurance_id, so it is only offered when the org
  // exposes at least one of them on its member profile. null = not loaded yet.
  const [orgHasInsuranceIds, setOrgHasInsuranceIds] = useState<boolean | null>(null);
  useEffect(() => {
    membersAPI
      .getProfileFields({ include_hidden: true })
      .then((rows) => {
        const hidden = new Set<string>();
        for (const r of rows || []) {
          if (r.is_core_field && r.is_hidden) hidden.add(r.field_key);
        }
        setHiddenCoreKeys(hidden);
        setOrgHasInsuranceIds(orgUsesInsuranceIds(rows));
      })
      .catch(() => { setHiddenCoreKeys(new Set()); setOrgHasInsuranceIds(null); });
  }, []);

  // dynamicFilterValues: map of field_key → string value selected by user.
  // Sent to backend as repeating custom_field=field_key=value query params.
  const [dynamicFilterValues, setDynamicFilterValues] = useState<Record<string, string>>({});
  useEffect(() => {
    membersAPI.getMemberListColumns('member')
      .then(setListColumns)
      .catch(() => setListColumns([]));
    membersAPI.getMemberAdvancedFilters('member')
      .then(setAdvancedFilterDefs)
      .catch(() => setAdvancedFilterDefs([]));
  }, []);

  // TRUS-568 — each user's own view: which of the org-configured columns and
  // filters they see. Defaults to the full org configuration.
  const {
    selection: viewFieldSelection,
    visibleColumns,
    visibleFilters,
    save: saveViewFields,
  } = useMemberListViewFields(listColumns, advancedFilterDefs);
  // A filter the user just removed from their view must not keep filtering
  // invisibly — drop its value.
  useEffect(() => {
    const visibleKeys = new Set(visibleFilters.map((f) => f.field_key));
    setDynamicFilterValues((prev) => {
      const stale = Object.keys(prev).filter((k) => !visibleKeys.has(k));
      if (stale.length === 0 || advancedFilterDefs.length === 0) return prev;
      const next = { ...prev };
      for (const k of stale) delete next[k];
      return next;
    });
  }, [visibleFilters, advancedFilterDefs.length]);
  // Same rule for the universal Language / Insurance dropdowns once they turn
  // out to be irrelevant for this org (a "Save my view" snapshot can restore a
  // value for a dropdown that is no longer rendered).
  const orgHasLanguageFilter = hasConfiguredLanguageFilter(advancedFilterDefs);
  useEffect(() => {
    if (orgHasLanguageFilter && filterLanguage) setFilterLanguage(undefined);
  }, [orgHasLanguageFilter, filterLanguage]);
  useEffect(() => {
    if (orgHasInsuranceIds === false && filterInsurance) setFilterInsurance(undefined);
  }, [orgHasInsuranceIds, filterInsurance]);

  // Per-user filter saves — server-side via /api/user-filter-preferences.
  // Travels across devices, supports role-default fallback (admin can set
  // a default for everyone with the same role). LocalStorage v1 retired.
  const FILTER_PAGE_KEY = 'member_list';
  // Hydrate saved view on mount
  useEffect(() => {
    apiClient.get(`/api/user-filter-preferences/${FILTER_PAGE_KEY}`)
      .then(r => {
        const v = r.data?.filter_state || {};
        if (v.selectedChipKey) setSelectedChipKey(v.selectedChipKey);
        if (v.filterProgramId) setFilterProgramId(v.filterProgramId);
        if (v.filterEnrollStatus) setFilterEnrollStatus(v.filterEnrollStatus);
        if (v.filterGender) setFilterGender(v.filterGender);
        if (v.filterLanguage) setFilterLanguage(v.filterLanguage);
        if (v.filterInsurance) setFilterInsurance(v.filterInsurance);
        if (v.filterCustomField) setFilterCustomField(v.filterCustomField);
        if (v.filterCoordinator) setFilterCoordinator(v.filterCoordinator);
        if (v.showAdvancedFilters !== undefined) setShowAdvancedFilters(v.showAdvancedFilters);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const saveCurrentView = async () => {
    try {
      await apiClient.put(`/api/user-filter-preferences/${FILTER_PAGE_KEY}`, {
        filter_state: {
          selectedChipKey, filterProgramId, filterEnrollStatus, filterGender,
          filterLanguage, filterInsurance, filterCustomField, filterCoordinator,
          showAdvancedFilters,
        },
      });
      alert('View saved. Next time you open the member list, these filters will be applied automatically (across all devices).');
    } catch (e: any) {
      alert(`Failed to save view: ${e?.response?.data?.detail || e?.message || 'unknown error'}`);
    }
  };
  const resetSavedView = async () => {
    try {
      await apiClient.delete(`/api/user-filter-preferences/${FILTER_PAGE_KEY}`);
      alert('Saved view cleared.');
    } catch (e: any) {
      alert(`Failed to clear: ${e?.response?.data?.detail || e?.message || 'unknown error'}`);
    }
  };

  // Fetch programs and users for filter dropdowns
  useEffect(() => {
    const cboId = localStorage.getItem('active_cbo_id');
    apiClient.get('/api/programs')
      .then(r => setPrograms(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    // staff_only drops portal members; exclude_internal drops platform admins
    // and Trust-WeaveIn automation accounts. The coordinator candidates are then
    // every remaining staff user of ANY role — see selectCoordinatorCandidates
    // (TRUS-434: custom role_terminology types like "dcm01" are staff too and
    // must not be filtered out by a fixed role allowlist).
    apiClient.get(`/api/cbos/${cboId}/users?staff_only=true&exclude_internal=true`)
      .then(r => {
        const data = r.data;
        const users = Array.isArray(data) ? data : (data?.users || []);
        setOrgUsers(selectCoordinatorCandidates(users));
      })
      .catch(() => {});
  }, []);

  // Fetch per-CBO configured filter chips. Empty array if the org hasn't
  // configured any — UI silently falls back to the default member view.
  useEffect(() => {
    apiClient.get('/api/members/filter-chips', { params: { include_counts: true } })
      .then(r => {
        const chips: FilterChipConfig[] = Array.isArray(r.data?.chips) ? r.data.chips : [];
        setFilterChips(chips);
        // Auto-select the default_on chip if one exists and nothing is selected —
        // but never one that matches nothing, which would open the page on an empty
        // member list and look like the roster had vanished.
        if (chips.length > 0 && !selectedChipKey) {
          const defaultChip = chips.find(c => c.default_on && c.count !== 0);
          if (defaultChip) setSelectedChipKey(defaultChip.key);
        }
      })
      .catch(() => setFilterChips([]));
    // selectedChipKey intentionally NOT in deps — only auto-select on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFilterCount = [filterProgramId, filterEnrollStatus, filterGender, filterLanguage, filterInsurance, filterCustomField, filterCoordinator].filter(Boolean).length;

  // Stable derived list of custom_field=field=value params. Memoized so the
  // reference only changes when filter values actually change — otherwise a
  // fresh array on every render would cascade through useMembers' deps and
  // refetch on every state tick, hanging the request queue → axios 30s
  // timeout. Stringifying dynamicFilterValues for the dep keeps the memo
  // stable across reference-equality re-renders.
  const dynamicFilterKey = JSON.stringify(dynamicFilterValues);
  const customFieldList = useMemo(() => {
    const list: string[] = [];
    if (filterCustomField) list.push(filterCustomField);
    for (const [k, v] of Object.entries(dynamicFilterValues)) {
      if (v) list.push(`${k}=${v}`);
    }
    return list.length === 0 ? undefined : list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCustomField, dynamicFilterKey]);

  const {
    members,
    total,
    page,
    loading,
    error,
    setPage,
    setSearch,
    deleteMember,
  } = useMembers({
    page_size: pageSize,
    search: searchQuery,
    program_id: filterProgramId,
    enrollment_status: filterEnrollStatus,
    gender: filterGender,
    language: filterLanguage,
    insurance_type: filterInsurance,
    // Merge the legacy single `filterCustomField` with the new dynamic
    // multi-filter map. Each non-empty value becomes one `custom_field=`
    // param; backend ANDs them. MUST be stable across renders — useMembers
    // uses this as a useCallback dep and a new array ref each render would
    // trigger an infinite refetch loop (axios 30s timeout symptom).
    custom_field: customFieldList,
    coordinator_id: filterCoordinator,
    filter_chip: selectedChipKey,
    membership_status: showArchived ? 'archived' : undefined,
    // LCC only for now (TODO: productize via nav registry)
    workforce_only: isLccWorkforce && workforceOnly ? true : undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  // Member favorites — drives the strip + the per-row star column.
  const { favoritesDetail, isFavorite, toggleFavorite } = useMemberFavorites();

  // When favorites filter is active, fetch favorite members individually
  // (they may not be on the current paginated page)
  const [favMembers, setFavMembers] = useState<Member[]>([]);
  useEffect(() => {
    if (!favoriteIds || favoriteIds.length === 0) { setFavMembers([]); return; }
    Promise.all(
      favoriteIds.map(id =>
        apiClient.get(`/api/members/${id}`)
          // GET /api/members/{id} returns a wrapper { member, enrollments } — unwrap
          // to the bare member so favorite rows render (not blank/filtered out).
          .then(r => (r.data?.member ?? r.data) ?? null)
          .catch(() => null)
      )
    ).then(results => setFavMembers(results.filter(Boolean) as Member[]));
  }, [favoriteIds]);

  // Fetch AI enrichment data for current members
  useEffect(() => {
    const fetchAIEnrichmentData = async () => {
      if (members.length === 0) return;

      try {
        const API_URL = productConfig.weaveinApi.baseUrl ?? 'http://localhost:8000';

        // Fetch AI tasks for member enrichment
        const response = await authFetch(`${API_URL}/api/ai-orchestration/tasks?category=data_enrichment&limit=100`);

        if (response.ok) {
          const data = await response.json();
          const enrichmentMap: Record<number, AIEnrichmentStatus> = {};

          // Map AI tasks to members
          members.forEach(member => {
            const memberTasks = data.tasks?.filter((task: any) =>
              task.related_entity_type === 'member' && task.related_entity_id === member.id
            ) || [];

            if (memberTasks.length > 0) {
              const latestTask = memberTasks[0]; // Assumes tasks are sorted by most recent
              enrichmentMap[member.id] = {
                has_enrichment: true,
                confidence_score: latestTask.confidence_score,
                last_enriched_at: latestTask.completed_at,
                status: latestTask.status
              };
            } else {
              enrichmentMap[member.id] = {
                has_enrichment: false,
                confidence_score: null,
                last_enriched_at: null,
                status: null
              };
            }
          });

          setAiEnrichmentData(enrichmentMap);
        }
      } catch (error) {
        console.error('Failed to fetch AI enrichment data:', error);
      }
    };

    fetchAIEnrichmentData();
    // Plan / LOB lookup — single call per page load, keyed by member_id.
    const cboId = localStorage.getItem('active_cbo_id') || '';
    if (cboId) {
      authFetch(`/api/orgs/${cboId}/members-plan-lookup`)
        .then(r => r.ok ? r.json() : [])
        .then((rows: MemberPlanLookup[]) => {
          const map: Record<number, MemberPlanLookup> = {};
          (Array.isArray(rows) ? rows : []).forEach(r => { map[r.member_id] = r; });
          setPlanLookup(map);
        })
        .catch(() => setPlanLookup({}));
    }
  }, [members]);

  // When the favorites filter is active (favoriteIds is a non-null array — even if
  // empty), show only the separately-fetched favorite members; an empty result then
  // reads as "no favorites yet" rather than silently falling back to all members.
  const baseMembers = favoriteIds != null ? favMembers : members;

  // Filter members by data quality score + LOB + plan (the last two are
  // client-side because the API doesn't yet support filtering on
  // member_plan_enrollments-derived fields).
  const filteredMembers = baseMembers.filter(member => {
    if (filterLob && planLookup[member.id]?.lob_name !== filterLob) return false;
    if (filterPlan && planLookup[member.id]?.product_name !== filterPlan) return false;
    if (qualityFilter === 'all') return true;
    if (!member.data_quality_score) return qualityFilter === 'low';

    if (qualityFilter === 'high') return member.data_quality_score >= 80;
    if (qualityFilter === 'medium') return member.data_quality_score >= 60 && member.data_quality_score < 80;
    if (qualityFilter === 'low') return member.data_quality_score < 60;
    return true;
  });

  // Dynamic columns: when the org has configured "Show in list" fields, render
  // those instead of the legacy fixed columns. LOB/Plan stays as an auto column
  // when any member actually has plan data (relevant to health-plan orgs only).
  const useDynamicCols = listColumns.length > 0;
  const hasPlanData = Object.keys(planLookup).length > 0;
  const PII_LIST_FIELDS = new Set(['first_name', 'last_name', 'email', 'phone_no', 'date_of_birth', 'dob', 'medicaid_id', 'medicare_id', 'ssn_last_four']);

  const listCellValue = (member: any, fieldKey: string): string => {
    switch (fieldKey) {
      case 'phone_no': return member.phone_number || member.phone_no || '';
      case 'date_of_birth': return formatDateOnly(member.date_of_birth || member.dob) || '';
      default: return member[fieldKey] ?? '';
    }
  };

  const renderListCell = (member: any, col: MemberListColumn) => {
    const raw = col.is_core_field ? listCellValue(member, col.field_key) : (member.custom_fields?.[col.field_key] ?? '');
    // Preserve the ECM-status colored badge when it's configured as a column.
    if (!col.is_core_field && col.field_key === 'enhanced_care_management_status') {
      const status = member.custom_fields?.enhanced_care_management_status;
      if (!status) return <span className="text-gray-400 text-xs">—</span>;
      const variant: any = status === 'EN' ? 'success'
        : status === 'EX' || status === 'TM' ? 'destructive'
        : status === 'OU' || status === 'OR' ? 'warning' // SFHP's outreach code is OU
        : 'default';
      return <Badge variant={variant} size="sm" className="text-[10px] font-mono">{status}</Badge>;
    }
    if (col.is_core_field && PII_LIST_FIELDS.has(col.field_key)) {
      const shieldName = col.field_key === 'date_of_birth' ? 'dob' : col.field_key;
      return (
        <ShieldedField
          value={raw}
          fieldName={shieldName}
          isShielded={member._shielded_fields?.includes(shieldName)}
          showRevealButton={false}
        />
      );
    }
    return raw !== '' && raw != null ? <span>{String(raw)}</span> : <span className="text-gray-400">—</span>;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchTerm);
    setPage(1);
  };

  const handleDelete = async (id: number, name: string) => {
    if (window.confirm(t('members.confirmDeleteMember', 'Are you sure you want to delete {{name}}?', { name }))) {
      const success = await deleteMember(id);
      if (success) {
        alert(t('members.deletedSuccessfully', 'Member deleted successfully'));
      }
    }
  };

  const handleMemberClick = (member: Member) => {
    if (onMemberSelect) {
      onMemberSelect(member);
    } else if (onNavigate) {
      onNavigate('members-view', member.id);
    }
  };

  // Duplicate-profile count for the toolbar (TRUS-425/426). Advisory only: a
  // failure here must never block the member list, so the error is swallowed and
  // the control simply stays hidden.
  const [duplicateCount, setDuplicateCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    membersAPI
      .listDuplicateMembers({ min_confidence: 'high', limit: 200 })
      .then((res) => {
        if (!cancelled) setDuplicateCount(res.total_duplicate_profiles || 0);
      })
      .catch(() => {
        if (!cancelled) setDuplicateCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const getQualityVariant = (score: number): 'success' | 'warning' | 'danger' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center justify-end gap-2">
        {/* Only shown once we know there is something to review, so orgs with a
            clean roster never see a control that leads to an empty screen. */}
        {duplicateCount > 0 && (
          <Button
            variant="secondary"
            icon={<Users className="w-4 h-4" />}
            onClick={() => onNavigate?.('members-duplicates')}
          >
            {t('members.review_duplicates', 'Review duplicates')} ({duplicateCount})
          </Button>
        )}
        <Button
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => onNavigate?.('members-new')}
        >
          {t('members.add_member', 'Add Member')}
        </Button>
      </div>

      {/* Filters Section */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <form onSubmit={handleSearch}>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  {/* This box had no type, name or autoComplete, and sat inside a
                      <form> with a placeholder reading "name, email, phone".
                      Chrome classifies an unlabelled text input by its placeholder,
                      decided this was a contact field, and offered its autofill —
                      which surfaces saved Google Pay profiles. Maui saw a GPay
                      dropdown over the roster search (TRUS-444).

                      type="search" states what it is, autoComplete="off" opts out,
                      and the deliberately non-semantic name leaves the heuristics
                      nothing contact-shaped to match. The data-* attributes do the
                      same for 1Password and LastPass, which classify on placeholder
                      text too. */}
                  <Input
                    type="search"
                    name="member-roster-query"
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    className="pl-10"
                    placeholder={t('members.searchPlaceholder', 'Search by name, email, phone, or Medicaid ID...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button type="submit" variant="primary">
                  {t('buttons.search', 'Search')}
                </Button>
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSearchTerm('');
                      setSearchQuery('');
                      setSearch('');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </form>
            <div className="flex items-center gap-2">
              {/* Workforce-only filter chip — LCC only for now (TODO: productize via nav registry) */}
              {isLccWorkforce && (
                <button
                  type="button"
                  onClick={() => setWorkforceOnly((v) => !v)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    workforceOnly
                      ? 'bg-v2accent text-white'
                      : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
                  }`}
                >
                  {t('members.workforceOnly', 'Workforce only')}
                </button>
              )}
              {/* Favorites filter chip (v2) */}
              {onToggleFavorites && (
                <button
                  type="button"
                  onClick={onToggleFavorites}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    favoriteIds
                      ? 'bg-amber-500 text-white'
                      : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-700'
                  }`}
                >
                  ★ {t('members.favorites', 'Favorites')}{favoriteIds ? ` (${favoriteIds.length})` : ''}
                </button>
              )}
              <Select
                value={qualityFilter}
                onValueChange={(value) => setQualityFilter(value as any)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder={t('members.dataQualityFilter', 'Data Quality Filter')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('members.allMembers', 'All Members')} ({members.length})
                  </SelectItem>
                  <SelectItem value="high">
                    {t('members.highQuality', 'High Quality (80%+)')}
                  </SelectItem>
                  <SelectItem value="medium">
                    {t('members.mediumQuality', 'Medium Quality (60-79%)')}
                  </SelectItem>
                  <SelectItem value="low">
                    {t('members.lowQuality', 'Low Quality (<60%)')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Tooltip
                content={`${t('members.qualityBreakdownTitle', 'Data Quality Score Breakdown:')}
• ${t('members.qualityBasicInfo', 'Basic Info (40pts): First name, Last name, DOB, Insurance ID')}
• ${t('members.qualityContactInfo', 'Contact Info (30pts): Phone (15pts), Email (15pts)')}
• ${t('members.qualityAddress', 'Address (20pts): Street, City, State, ZIP (5pts each)')}
• ${t('members.qualityAdditional', 'Additional (10pts): Preferred language, Custom fields (5pts each)')}
${t('members.qualityRanges', 'High: 80-100% | Medium: 60-79% | Low: <60%')}`}
              >
                <Button variant="ghost" size="icon">
                  <span className="text-sm font-bold text-gray-500">ⓘ</span>
                </Button>
              </Tooltip>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Toggle + Panel */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            activeFilterCount > 0
              ? 'bg-v2accent text-white'
              : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
          {t('members.advancedFilters', 'Advanced Filters')} {(() => {
            const total = activeFilterCount + (selectedChipKey ? 1 : 0);
            return total > 0 ? `(${total})` : '';
          })()}
        </button>
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={() => { setFilterProgramId(undefined); setFilterEnrollStatus(undefined); setFilterGender(undefined); setFilterLanguage(undefined); setFilterInsurance(undefined); setFilterCustomField(undefined); setFilterCoordinator(undefined); setSelectedChipKey(undefined); setDynamicFilterValues({}); }}
            className="ml-2 text-xs text-red-500 hover:text-red-700"
          >
            {t('members.clearAllFilters', 'Clear all filters')}
          </button>
        )}
        <button
          type="button"
          onClick={() => { setShowArchived(!showArchived); setPage(1); }}
          aria-pressed={showArchived}
          className={`ml-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            showArchived
              ? 'bg-amber-500 text-white'
              : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
          }`}
          title={t('members.archivedToggleHelp', 'Show members that were archived (deleted) in this organization, so you can restore them')}
        >
          <Archive className="w-3.5 h-3.5" />
          {showArchived
            ? t('members.viewingArchived', 'Viewing archived')
            : t('members.showArchived', 'Archived')}
        </button>
        <button
          type="button"
          onClick={saveCurrentView}
          className="ml-2 text-xs text-blue-600 hover:text-blue-800"
          title="Save current filter selection as your default view for this org"
        >
          {t('members.saveMyView', 'Save my view')}
        </button>
        <button
          type="button"
          onClick={resetSavedView}
          className="ml-2 text-xs text-gray-500 hover:text-gray-700"
          title="Forget the saved default view"
        >
          {t('members.resetSavedView', 'Reset saved view')}
        </button>
        <MemberListViewCustomizer
          orgColumns={listColumns}
          orgFilters={advancedFilterDefs}
          selection={viewFieldSelection}
          onSave={saveViewFields}
        />
      </div>

      {showArchived && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-700 dark:bg-amber-900/30">
          <strong>{t('members.archivedViewTitle', 'Showing archived members.')}</strong>{' '}
          {t(
            'members.archivedViewHelp',
            'These were deleted from your member list, but nothing was lost — open one and use Restore to put it back.',
          )}
        </div>
      )}

      {showAdvancedFilters && (
        <Card className="mb-4 border-blue-200 bg-blue-50/30">
          <CardContent className="p-4">
            {/* Per-CBO configured Quick Filters (moved from top-row to keep header tidy).
                Configured via cbo.settings.member_list_filter_chips. */}
            {filterChips.length > 0 && (
              <div className="mb-3 pb-3 border-b border-blue-200">
                <label className="text-[10px] font-medium text-gray-500 mb-2 block uppercase tracking-wider">
                  Quick Filters
                </label>
                <div className="flex flex-wrap gap-2">
                  {filterChips.map((chip) => {
                    const isActive = selectedChipKey === chip.key;
                    // A chip that matches nothing stays clickable — the user should be
                    // able to confirm the empty result for themselves — but it reads as
                    // empty before they click, rather than after.
                    const isEmpty = chip.count === 0;
                    const colorClasses = isActive
                      ? 'bg-v2accent text-white border-v2accent'
                      : isEmpty
                        ? 'bg-surface text-ink-300 border-v2border border-dashed'
                        : 'bg-surface text-ink-500 border-v2border hover:text-ink';
                    const emptyHint = chip.predicate_ok === false
                      ? t('members.chipMisconfigured', 'This filter is mis-configured and matches no members. Ask an admin to review it.')
                      : t('members.chipNoMatches', 'No members match this filter right now.');
                    return (
                      <button
                        key={chip.key}
                        onClick={() => setSelectedChipKey(isActive ? undefined : chip.key)}
                        title={isEmpty ? emptyHint : (chip.description || '')}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${colorClasses}`}
                        type="button"
                      >
                        <span>{chip.label}</span>
                        {typeof chip.count === 'number' && (
                          <span className="tabular-nums opacity-70">{chip.count}</span>
                        )}
                        {isActive && <X className="w-3 h-3" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Org-configured advanced filter inputs (from member_field_configurations
                show_in_advanced_filter=true). Renders BEFORE the universal hardcoded
                filters so admins control the priority surface. */}
            {visibleFilters.length > 0 && (
              <div className="mb-3 pb-3 border-b border-blue-200">
                <label className="text-[10px] font-medium text-gray-500 mb-2 block uppercase tracking-wider">
                  Field-config filters
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {visibleFilters.map((f) => {
                    const setVal = (v: string) => {
                      setDynamicFilterValues(prev => {
                        const next = { ...prev };
                        if (!v) delete next[f.field_key];
                        else next[f.field_key] = v;
                        return next;
                      });
                      setPage(1);
                    };
                    const value = dynamicFilterValues[f.field_key] || '';
                    // Prefer admin-enumerated options; fall back to the
                    // backend-derived distinct_values when the admin didn't
                    // explicitly enumerate. Either source → render dropdown.
                    const dropdownOptions: Array<{ value: string; label: string }> = (() => {
                      if (f.options && f.options.length > 0) return f.options;
                      if (f.distinct_values && f.distinct_values.length > 0) {
                        return f.distinct_values.map((v) => ({ value: v, label: v }));
                      }
                      return [];
                    })();
                    return (
                      <div key={f.field_key}>
                        <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">
                          {f.display_name}
                        </label>
                        {dropdownOptions.length > 0 ? (
                          <select
                            value={value}
                            onChange={(e) => setVal(e.target.value)}
                            className="w-full h-8 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 bg-white dark:bg-gray-800"
                          >
                            <option value="">Any</option>
                            {dropdownOptions.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            value={value}
                            onChange={(e) => setVal(e.target.value)}
                            placeholder={`Filter ${f.display_name.toLowerCase()}…`}
                            className="h-8 text-xs"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Domain dropdowns — relational filters (Program, Enrollment,
                Plan, Coordinator) that don't map onto member field config.
                Generic field-driven filters (Gender / Language / Insurance)
                are HIDDEN here when the admin has already added them to the
                Field-config filters set above, to avoid duplicate controls. */}
            {(() => {
              // Hide a hardcoded dropdown if EITHER:
              //   (a) the same field already appears in the Field-config
              //       filters above (avoid duplicate controls), OR
              //   (b) the underlying core field is is_hidden in field config
              //       (admin hid the field from the profile; surfacing it as
              //       a filter contradicts that choice). IFR hides every
              //       demographic core field — they only use custom fields.
              const fcKeys = new Set(advancedFilterDefs.map((f) => f.field_key));
              const showGender = !fcKeys.has('gender') && !hiddenCoreKeys.has('gender');
              // TRUS-568: ANY org-configured language field (primary_language,
              // member_primary_language, a field labelled "… Language", …)
              // replaces the universal en/es/zh list, which does not match the
              // values the org actually records (Maui's Pacific-Islander list).
              const showLanguage =
                !orgHasLanguageFilter &&
                !hiddenCoreKeys.has('language') &&
                !hiddenCoreKeys.has('preferred_language');
              // Insurance is not in member_core_fields (it's derived from
              // medicaid_id / medicare_id / commercial_insurance_id presence),
              // so there's no is_hidden flag for it. Hide it when ANY of the
              // three insurance id fields is hidden — that's the closest
              // signal the admin doesn't want insurance-driven filtering.
              // TRUS-568: shown only once profile fields confirm the org
              // exposes at least one insurance id (unconfigured orgs get the
              // core fields visible by default, so healthcare orgs keep it).
              const showInsurance =
                !fcKeys.has('insurance') &&
                !fcKeys.has('insurance_type') &&
                orgHasInsuranceIds === true;
              // Line of Business / Plan come from the org-wide plan lookup
              // (ACTIVE member_plan_enrollments). No enrollments → no options →
              // the filter could only ever return everything, so hide it.
              const lobOptions = distinctPlanValues(planLookup, 'lob_name');
              const planOptions = distinctPlanValues(planLookup, 'product_name', filterLob);
              const showLob = lobOptions.length > 0;
              const showPlan = distinctPlanValues(planLookup, 'product_name').length > 0;
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {/* Program */}
                  <div>
                    <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">Program</label>
                    <Select value={filterProgramId ? String(filterProgramId) : 'all'} onValueChange={(v) => { setFilterProgramId(v === 'all' ? undefined : Number(v)); setPage(1); }}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Programs</SelectItem>
                        {programs.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Enrollment Status */}
                  <div>
                    <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">Enrollment</label>
                    <Select value={filterEnrollStatus || 'all'} onValueChange={(v) => { setFilterEnrollStatus(v === 'all' ? undefined : v); setPage(1); }}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="discharged">Discharged</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Gender */}
                  {showGender && (
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">Gender</label>
                      <Select value={filterGender || 'all'} onValueChange={(v) => { setFilterGender(v === 'all' ? undefined : v); setPage(1); }}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="non_binary">Non-Binary</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Language */}
                  {showLanguage && (
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">Language</label>
                      <Select value={filterLanguage || 'all'} onValueChange={(v) => { setFilterLanguage(v === 'all' ? undefined : v); setPage(1); }}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="es">Spanish</SelectItem>
                          <SelectItem value="zh">Chinese</SelectItem>
                          <SelectItem value="vi">Vietnamese</SelectItem>
                          <SelectItem value="ko">Korean</SelectItem>
                          <SelectItem value="tl">Tagalog</SelectItem>
                          <SelectItem value="ar">Arabic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Insurance Type */}
                  {showInsurance && (
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">Insurance</label>
                      <Select value={filterInsurance || 'all'} onValueChange={(v) => { setFilterInsurance(v === 'all' ? undefined : v); setPage(1); }}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="medicaid">Medicaid</SelectItem>
                          <SelectItem value="medicare">Medicare</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Line of Business — populated from the org's plan enrollments */}
                  {showLob && (
                  <div>
                    <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">Line of Business</label>
                    <Select value={filterLob || 'all'} onValueChange={(v) => { setFilterLob(v === 'all' ? undefined : v); setPage(1); }}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All LOBs" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All LOBs</SelectItem>
                        {lobOptions.map(name => (
                          <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {/* Plan / Product */}
                  {showPlan && (
                  <div>
                    <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">Plan / Product</label>
                    <Select value={filterPlan || 'all'} onValueChange={(v) => { setFilterPlan(v === 'all' ? undefined : v); setPage(1); }}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All Plans" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Plans</SelectItem>
                        {planOptions.map(name => (
                          <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {/* Legacy "Custom Field" single-field picker retired —
                      superseded by the Field-config filters section above
                      (per-field, with admin-driven dropdowns/distinct values).
                      Admin can flag any custom field as a filter in Field
                      Configuration → Filter icon. */}

                  {/* Coordinator */}
                  <div>
                    <label className="text-[10px] font-medium text-gray-500 mb-1 block uppercase tracking-wider">Coordinator</label>
                    <Select value={filterCoordinator ? String(filterCoordinator) : 'all'} onValueChange={(v) => { setFilterCoordinator(v === 'all' ? undefined : Number(v)); setPage(1); }}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Coordinators</SelectItem>
                        {orgUsers.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.name}{u.role ? ` (${u.role})` : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Favorites Strip — quick access to starred members (hidden in v2 — uses filter chip instead) */}
      {favoritesDetail.length > 0 && !onToggleFavorites && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            {t('members.favorite_members', 'Favorite Members')} ({favoritesDetail.length})
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {favoritesDetail.map(fav => (
              <div
                key={`fav-${fav.member_id}`}
                onClick={() => onNavigate?.('members-view', fav.member_id)}
                className="flex-shrink-0 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 py-2 cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors min-w-[200px] max-w-[260px]"
              >
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(fav.member_id); }}
                    className="flex-shrink-0"
                    aria-label="Remove from favorites"
                  >
                    <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                  </button>
                  <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                    {fav.member_name}
                  </p>
                </div>
                {(fav.program_name || fav.assigned_name) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                    {fav.program_name}{fav.assigned_name ? ` · CM: ${fav.assigned_name}` : ''}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>{t('common.error', 'Error')}:</strong> {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            {t('members.loadingMembers', 'Loading members...')}
          </p>
        </div>
      )}

      {/* Members Table */}
      {!loading && filteredMembers.length > 0 && (
        <>
          <div className="v2-member-table rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto bg-white dark:bg-gray-800">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 dark:bg-gray-900">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>{t('members.memberNumber', 'Member #')}</TableHead>
                  <TableHead>
                    {/* Clicking Name cycles last name A-Z -> Z-A -> first name A-Z
                        -> Z-A. A button, not a bare onClick on the header cell, so
                        it is reachable by keyboard and announced as a control;
                        aria-sort tells a screen reader which way the column runs. */}
                    <button
                      type="button"
                      onClick={() => {
                        if (sortOrder === 'asc') {
                          setSortOrder('desc');
                        } else {
                          setSortBy(sortBy === 'last_name' ? 'first_name' : 'last_name');
                          setSortOrder('asc');
                        }
                        // The sort is server-side and paginated, so a changed order
                        // makes the current page meaningless — go back to page 1.
                        setPage(1);
                      }}
                      aria-sort={sortOrder === 'asc' ? 'ascending' : 'descending'}
                      title={
                        sortBy === 'last_name'
                          ? t('members.sortedByLastName', 'Sorted by last name — click to change')
                          : t('members.sortedByFirstName', 'Sorted by first name — click to change')
                      }
                      className="inline-flex items-center gap-1 hover:text-v2accent focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent rounded"
                    >
                      {sortBy === 'last_name'
                        ? t('members.nameLast', 'Name (last)')
                        : t('members.nameFirst', 'Name (first)')}
                      {sortOrder === 'asc' ? (
                        <ArrowUp className="w-3 h-3" aria-hidden="true" />
                      ) : (
                        <ArrowDown className="w-3 h-3" aria-hidden="true" />
                      )}
                    </button>
                  </TableHead>
                  {useDynamicCols ? (
                    <>
                      {hasPlanData && <TableHead>{t('members.plan', 'Line of Business / Plan')}</TableHead>}
                      {visibleColumns.map((col) => (
                        <TableHead key={col.field_key}>{col.display_name}</TableHead>
                      ))}
                    </>
                  ) : (
                    // Default fallback (org hasn't configured list columns):
                    // universal core fields only. Org-specific columns like
                    // CIN/MCP/LCM/ECM-Status come from field config.
                    <>
                      <TableHead>{t('members.plan', 'Line of Business / Plan')}</TableHead>
                      <TableHead>{t('common.email', 'Email')}</TableHead>
                      <TableHead>{t('common.phone', 'Phone')}</TableHead>
                      <TableHead>{t('members.dateOfBirth', 'Date of Birth')}</TableHead>
                    </>
                  )}
                  <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="pr-0">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(member.id); }}
                        className="p-0.5 hover:scale-110 transition-transform flex-shrink-0"
                        title={isFavorite(member.id) ? 'Remove from favorites' : 'Add to favorites'}
                        aria-label={isFavorite(member.id) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star
                          className={`w-4 h-4 ${
                            isFavorite(member.id)
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-gray-300 dark:text-gray-600 hover:text-yellow-500'
                          }`}
                        />
                      </button>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {member.member_number || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <ShieldIndicator isShielded={member._shielded} />
                        <Button
                          variant="link"
                          onClick={() => handleMemberClick(member)}
                          className="font-medium p-0 h-auto"
                        >
                          <ShieldedField
                            value={`${member.first_name} ${member.last_name}`}
                            fieldName="first_name"
                            isShielded={member._shielded_fields?.includes('first_name')}
                            showRevealButton={false}
                          />
                        </Button>
                        {member.membership_source === 'program_network' && (
                          <span
                            title="Reached via a hub-owned program through a participating org"
                            className="ml-1 inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:ring-indigo-800"
                          >
                            via network
                          </span>
                        )}
                        {/* Workforce indicator — LCC only for now (TODO: productize via nav registry) */}
                        {isLccWorkforce && member.is_workforce && (
                          <span
                            title="Workforce member"
                            className="ml-1 inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:ring-emerald-800"
                          >
                            {t('members.workforce', 'Workforce')}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    {useDynamicCols ? (
                      <>
                        {hasPlanData && (
                          <TableCell className="text-gray-700 dark:text-gray-300">
                            {planLookup[member.id] ? (
                              <div className="flex flex-col leading-tight">
                                <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                                  {planLookup[member.id].lob_name || '-'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {planLookup[member.id].product_name || '-'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.map((col) => (
                          <TableCell key={col.field_key} className="text-gray-700 dark:text-gray-300 text-sm max-w-[180px] truncate">
                            {renderListCell(member, col)}
                          </TableCell>
                        ))}
                      </>
                    ) : (
                      <>
                        <TableCell className="text-gray-700 dark:text-gray-300">
                          {planLookup[member.id] ? (
                            <div className="flex flex-col leading-tight">
                              <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                                {planLookup[member.id].lob_name || '-'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {planLookup[member.id].product_name || '-'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-700 dark:text-gray-300">
                          <ShieldedField
                            value={member.email}
                            fieldName="email"
                            isShielded={member._shielded_fields?.includes('email')}
                            showRevealButton={false}
                          />
                        </TableCell>
                        <TableCell className="text-gray-700 dark:text-gray-300">
                          <ShieldedField
                            value={member.phone_number || member.phone_no}
                            fieldName="phone_no"
                            isShielded={member._shielded_fields?.includes('phone_no')}
                            showRevealButton={false}
                          />
                        </TableCell>
                        <TableCell className="text-gray-700 dark:text-gray-300">
                          <ShieldedField
                            value={formatDateOnly(member.date_of_birth || member.dob)}
                            fieldName="dob"
                            isShielded={member._shielded_fields?.includes('dob')}
                            showRevealButton={false}
                          />
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Tooltip content={t('members.editMember', 'Edit Member')}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onNavigate?.('members-view', member.id)}
                          >
                            <Pencil className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </Button>
                        </Tooltip>
                        <Tooltip content={t('members.deleteMember', 'Delete Member')}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(member.id, `${member.first_name} ${member.last_name}`)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                          </Button>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex flex-col md:flex-row gap-4 items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('common.showing', 'Showing')} {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} {t('common.of', 'of')} {total} {t('members.title', 'members').toLowerCase()}
            </p>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              showPageSize={false}
              showItemCount={false}
            />
          </div>
        </>
      )}

      {/* Empty State - No Filter Results */}
      {!loading && members.length > 0 && filteredMembers.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {t('members.noMembersMatchFilter', 'No members match the selected quality filter')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('members.tryDifferentFilter', 'Try selecting a different data quality filter or clear your search.')}
            </p>
            <Button variant="primary" onClick={() => setQualityFilter('all')}>
              {t('members.showAllMembers', 'Show All Members')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State - No Members */}
      {!loading && members.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {t('members.no_members', 'No members found')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {searchQuery
                ? t('members.adjustSearchCriteria', 'Try adjusting your search criteria')
                : t('members.getStartedAddMember', 'Get started by adding your first member')}
            </p>
            {!searchQuery && (
              <Button
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => onNavigate?.('members-new')}
              >
                {t('members.addFirstMember', 'Add First Member')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
